import React, { useState } from "react";
import { toast, Toaster } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { MapPin, Phone, EnvelopeSimple, WhatsappLogo, NavigationArrow } from "@phosphor-icons/react";
import Reveal from "../components/Reveal";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";
import { useT } from "../lib/i18n";
import { trackEvent } from "../lib/analytics";

// Prayagraj city-centre map embed (no API key required for basic iframe embed)
const MAP_QUERY = "Yuva+Ayurvedic+Clinic+Prayagraj+Uttar+Pradesh";
const MAP_EMBED = `https://www.google.com/maps?q=${MAP_QUERY}&output=embed`;
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${MAP_QUERY}`;

export default function Contact() {
  useSeo({
    title: "Book Appointment | Yuva Ayurvedic Clinic Prayagraj | Call Now",
    absoluteTitle: true,
    description: "Book an appointment at Yuva Ayurvedic Clinic Private Limited, Prayagraj. Call +91 95190 78666, WhatsApp or message us. We reply personally — usually within a few hours. Prayagraj, Uttar Pradesh, India.",
    keywords: "Book appointment Ayurvedic clinic Prayagraj, Contact Yuva Ayurvedic, Men's health clinic Prayagraj contact, Ayurveda consultation Prayagraj",
  });
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error(t("contact.err.required")); return; }
    setLoading(true);
    try {
      await api.post("/contacts", form);
      trackEvent("contact_submitted", { subject: form.subject || "General enquiry" });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (e) {
      const msg = e?.response?.status === 429
        ? (e?.response?.data?.detail || t("contact.err.rate"))
        : (e?.response?.data?.detail || t("contact.err.generic"));
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="pb-24" data-testid="contact-page">
      <Toaster richColors position="top-center"/>
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <Reveal>
              <div className="overline">{t("contact.overline")}</div>
              <h1 className="font-serif-display text-5xl sm:text-6xl mt-6 leading-[0.95]">
                {t("contact.title.l1")}<br/>{t("contact.title.l2")} <span className="italic text-terracotta">{t("contact.title.l3")}</span>
              </h1>
              <p className="mt-8 text-muted-foreground">{t("contact.blurb")}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-12 space-y-6">
                <li className="flex gap-4"><MapPin size={22} weight="duotone" className="text-sage shrink-0 mt-1"/><div><div className="font-medium">{t("contact.block.address")}</div><div className="text-muted-foreground text-sm mt-1" style={{ whiteSpace: "pre-line" }}>{t("contact.block.address.value")}</div></div></li>
                <li className="flex gap-4"><Phone size={22} weight="duotone" className="text-sage shrink-0 mt-1"/><div><div className="font-medium">{t("contact.block.call")}</div><a href="tel:+919519078666" className="text-muted-foreground text-sm mt-1 block hover:text-terracotta">+91 95190 78666</a></div></li>
                <li className="flex gap-4"><EnvelopeSimple size={22} weight="duotone" className="text-sage shrink-0 mt-1"/><div><div className="font-medium">{t("contact.block.email")}</div><a href="mailto:info@yuvaayurvedic.com" className="text-muted-foreground text-sm mt-1 block hover:text-terracotta">info@yuvaayurvedic.com</a></div></li>
                <li className="flex gap-4"><WhatsappLogo size={22} weight="duotone" className="text-sage shrink-0 mt-1"/><div><div className="font-medium">{t("contact.block.whatsapp")}</div><a href="https://wa.me/919519078666" target="_blank" rel="noopener noreferrer" className="text-muted-foreground text-sm mt-1 block hover:text-terracotta">{t("cta.chat")}</a></div></li>
              </ul>
            </Reveal>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={onSubmit} className="bg-ivory border border-border/50 rounded-2xl p-8 lg:p-10 space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>{t("contact.field.name")}</Label><Input data-testid="contact-name" value={form.name} onChange={set("name")} className="bg-cream h-12 mt-2" required/></div>
                <div><Label>{t("contact.field.phone")}</Label><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="bg-cream h-12 mt-2"/></div>
                <div className="sm:col-span-2"><Label>{t("contact.field.email")}</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className="bg-cream h-12 mt-2" required/></div>
                <div className="sm:col-span-2"><Label>{t("contact.field.subject")}</Label><Input data-testid="contact-subject" value={form.subject} onChange={set("subject")} className="bg-cream h-12 mt-2" placeholder={t("contact.field.subject.placeholder")}/></div>
                <div className="sm:col-span-2"><Label>{t("contact.field.message")}</Label><Textarea data-testid="contact-message" value={form.message} onChange={set("message")} className="bg-cream mt-2 min-h-[140px]" required/></div>
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full rounded-full bg-forest text-cream py-4 hover:bg-terracotta transition-all duration-500 disabled:opacity-60">
                {loading ? t("cta.sending") : t("cta.sendMessage")}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-8" data-testid="contact-map-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div>
                <div className="overline">{t("contact.map.overline")}</div>
                <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 leading-tight">{t("contact.map.title.l1")} <span className="italic text-terracotta">{t("contact.map.title.l2")}</span></h2>
              </div>
              <a
                href={MAP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-directions-link"
                className="inline-flex items-center gap-2 rounded-full border border-forest text-forest px-5 py-2.5 text-sm hover:bg-forest hover:text-cream transition-all"
              >
                <NavigationArrow size={16} weight="bold"/> {t("cta.getDirections")}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl overflow-hidden border border-border/60 shadow-sm bg-ivory">
              <iframe
                src={MAP_EMBED}
                title="Yuva Ayurvedic Clinic Private Limited location"
                width="100%"
                height="420"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="contact-map-iframe"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
